import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import BeamWebhookReplayService from './BeamWebhookReplayService';
import { formatAmount } from '../../../utils/formatAmount';

const TYPE_LABEL = {
  orderPayment: 'จ่าย order',
  walletTopup: 'เติม wallet',
};

const formatThaiTime = (value) =>
  new Date(value).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'short', timeStyle: 'short' });

/** Bangkok date 'YYYY-MM-DD', daysAgo days before today */
const bangkokDate = (daysAgo = 0) =>
  new Date(Date.now() + 7 * 60 * 60 * 1000 - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

/**
 * Superuser: Beam payments still 'pending' in the chosen dates (default: last 3 days,
 * max 31). If Beam's webhook never got through (it retries up to 10 times, then stops), "ตรวจกับ Beam" asks Beam for the real
 * status and, only when Beam says paid, runs the same processing as the webhook.
 * A late real webhook afterwards is ignored by the backend, so nothing is created twice.
 */
const BeamWebhookReplay = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [manualId, setManualId] = useState('');
  const [results, setResults] = useState({}); // beamPurchaseId -> { ok, text }
  const [from, setFrom] = useState(() => bangkokDate(2));
  const [to, setTo] = useState(() => bangkokDate(0));

  const fetchPending = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const res = await BeamWebhookReplayService.getPending({ from, to });
      setPurchases(res.data.purchases || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'โหลดรายการไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleReplay = async (beamPurchaseId) => {
    const id = String(beamPurchaseId || '').trim();
    if (!id) return;
    if (!window.confirm(`ตรวจสถานะกับ Beam สำหรับ ${id}?\nถ้า Beam ยืนยันว่าจ่ายแล้ว ระบบจะสร้าง order / บันทึกยอดให้ทันที`)) return;

    setBusyId(id);
    try {
      const res = await BeamWebhookReplayService.replay(id);
      const text = `สำเร็จ — Beam: ${res.data.beamStatus}, สถานะ: ${res.data.status}`;
      setResults((r) => ({ ...r, [id]: { ok: true, text } }));
      toast.success(text);
      fetchPending();
    } catch (err) {
      const text = err.response?.data?.message || 'ทำรายการไม่สำเร็จ';
      setResults((r) => ({ ...r, [id]: { ok: false, text } }));
      toast.error(text);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold">Beam webhook ค้าง</h1>
      <p className="mt-1 text-sm text-gray-600">
        รายการชำระ Beam ที่ยังเป็น pending ในช่วงวันที่ที่เลือก (ไม่เกิน 31 วัน) — กด "ตรวจกับ Beam" ระบบจะถาม Beam ก่อน
        และประมวลผลเฉพาะรายการที่ Beam ยืนยันว่าจ่ายแล้วเท่านั้น (ลิงก์ที่ลูกค้ายังไม่จ่ายจะไม่ถูกแตะ)
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          placeholder="Beam ID (เช่น 74fIzfz8Q4)"
          className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => handleReplay(manualId)}
          disabled={!manualId.trim() || busyId !== null}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          ตรวจกับ Beam
        </button>
        <label className="ml-2 text-sm text-gray-600">จากวันที่</label>
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <label className="text-sm text-gray-600">ถึงวันที่</label>
        <input
          type="date"
          value={to}
          min={from}
          max={bangkokDate(0)}
          onChange={(e) => setTo(e.target.value)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={fetchPending}
          disabled={loading}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
        >
          รีเฟรช
        </button>
      </div>
      {results[manualId.trim()] && (
        <p className={`mt-2 text-sm ${results[manualId.trim()].ok ? 'text-green-700' : 'text-red-700'}`}>
          {results[manualId.trim()].text}
        </p>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full border border-gray-200 text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2">สร้างเมื่อ</th>
              <th className="px-3 py-2">Beam ID</th>
              <th className="px-3 py-2">ประเภท</th>
              <th className="px-3 py-2">ช่องทาง</th>
              <th className="px-3 py-2 text-right">ยอด (฿)</th>
              <th className="px-3 py-2 text-right">รายการสั่งซื้อ</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">กำลังโหลด...</td></tr>
            ) : purchases.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">ไม่มีรายการค้างในช่วงนี้</td></tr>
            ) : (
              purchases.map((p) => {
                const result = results[p.beamPurchaseId];
                return (
                  <tr key={p.id} className="border-t border-gray-200">
                    <td className="px-3 py-2 whitespace-nowrap">{formatThaiTime(p.createdAt)}</td>
                    <td className="px-3 py-2 font-mono">{p.beamPurchaseId}</td>
                    <td className="px-3 py-2">{TYPE_LABEL[p.paymentType] || p.paymentType}{p.note ? ` · ${p.note}` : ''}</td>
                    <td className="px-3 py-2">{p.sourceApi === 'charge' ? 'QR' : 'บัตร / ลิงก์'}</td>
                    <td className="px-3 py-2 text-right">{formatAmount(p.originalAmount)}</td>
                    <td className="px-3 py-2 text-right">{p.prePaymentItems || '-'}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleReplay(p.beamPurchaseId)}
                        disabled={busyId !== null}
                        className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      >
                        {busyId === p.beamPurchaseId ? 'กำลังตรวจ...' : 'ตรวจกับ Beam'}
                      </button>
                      {result && (
                        <div className={`mt-1 text-xs ${result.ok ? 'text-green-700' : 'text-red-700'}`}>{result.text}</div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BeamWebhookReplay;
